import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Paperclip, MessageSquare, Filter, User, Trash2, MapPin, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TaskDrawer from "@/components/TaskDrawer";
import TaskImportDialog from "@/components/TaskImportDialog";
import { PRIORITIES, priorityColor } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";

const UNASSIGNED = "__unassigned__";
const NONE = "__none__";
const ALL = "__all__";
const GENERAL = "__general__";

export default function Tasks() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [filterCat, setFilterCat] = useState(ALL);
  const [filterLoc, setFilterLoc] = useState(ALL);
  const [mineOnly, setMineOnly] = useState(false);
  const dragTaskId = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const moveTask = async (taskId, statusId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status_id === statusId) return;
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status_id: statusId } : t)));
    try { await api.patch(`/tasks/${taskId}`, { status_id: statusId }); toast.success("Task moved"); }
    catch (e) { toast.error("Move failed"); loadTasks(); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Task deleted"); setTasks((prev) => prev.filter((t) => t.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadRef = useCallback(async () => {
    const [s, c, u, l] = await Promise.all([api.get("/task-statuses"), api.get("/task-categories"), api.get("/users"), api.get("/locations")]);
    setStatuses(s.data); setCategories(c.data); setUsers(u.data); setLocations(l.data);
  }, []);
  const loadTasks = useCallback(async () => {
    const t = await api.get("/tasks"); setTasks(t.data); setLoading(false);
  }, []);
  useEffect(() => { loadRef(); loadTasks(); }, [loadRef, loadTasks]);

  const [form, setForm] = useState({ title: "", description: "", category_id: NONE, status_id: NONE, assignee_id: UNASSIGNED, location_id: GENERAL, priority: "medium", due_date: "" });
  const [saving, setSaving] = useState(false);

  const createTask = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await api.post("/tasks", {
        title: form.title, description: form.description,
        category_id: form.category_id === NONE ? null : form.category_id,
        status_id: form.status_id === NONE ? (statuses[0]?.id || null) : form.status_id,
        assignee_id: form.assignee_id === UNASSIGNED ? null : form.assignee_id,
        location_id: form.location_id === GENERAL ? null : form.location_id,
        priority: form.priority, due_date: form.due_date || null,
      });
      toast.success("Task created");
      setCreateOpen(false);
      setForm({ title: "", description: "", category_id: NONE, status_id: NONE, assignee_id: UNASSIGNED, location_id: GENERAL, priority: "medium", due_date: "" });
      loadTasks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name;
  const locName = (id) => locations.find((l) => l.id === id)?.name;
  let visibleTasks = tasks;
  if (mineOnly && user) visibleTasks = visibleTasks.filter((t) => t.assignee_id === user.id);
  if (filterCat !== ALL) visibleTasks = visibleTasks.filter((t) => (filterCat === NONE ? !t.category_id : t.category_id === filterCat));
  if (filterLoc !== ALL) visibleTasks = visibleTasks.filter((t) => (filterLoc === GENERAL ? !t.location_id : t.location_id === filterLoc));
  const columns = statuses.length ? statuses : [{ id: null, name: "All Tasks", color: "#a1a1aa" }];

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-6 py-3">
        <div className="flex items-center gap-3">
          <p className="text-sm text-zinc-500">{visibleTasks.length} tasks</p>
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-zinc-400" />
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger data-testid="task-category-filter" className="h-8 w-44 rounded-none text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                <SelectItem value={NONE}>Uncategorized</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-zinc-400" />
            <Select value={filterLoc} onValueChange={setFilterLoc}>
              <SelectTrigger data-testid="task-location-filter" className="h-8 w-44 rounded-none text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All locations</SelectItem>
                <SelectItem value={GENERAL}>General (no location)</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={() => setMineOnly((v) => !v)}
            data-testid="my-tasks-toggle"
            className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors ${mineOnly ? "border-[#e84e34] bg-[#e84e34] text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}
          >
            <User className="h-3.5 w-3.5" /> My Tasks
          </button>
        </div>
        <div className="flex items-center gap-2">
          {canManage && <button onClick={() => setImportOpen(true)} data-testid="import-tasks-button" className="flex items-center gap-2 border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]">
            <Upload className="h-4 w-4" /> Import CSV
          </button>}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <button data-testid="create-task-button" className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#c73e26]">
                <Plus className="h-4 w-4" /> New Task
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Create task</DialogTitle>
              <DialogDescription>Add a new task, assign it and set its status.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34] focus:ring-1 focus:ring-[#e84e34]" />
              <textarea data-testid="task-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34] focus:ring-1 focus:ring-[#e84e34]" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.status_id} onValueChange={(v) => setForm({ ...form, status_id: v })}>
                  <SelectTrigger data-testid="task-status-select" className="rounded-none"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Default status</SelectItem>
                    {statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="task-category-select" className="rounded-none"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No category</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="task-assignee-select" className="rounded-none"><SelectValue placeholder="Assignee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                  <SelectTrigger data-testid="task-location-select" className="rounded-none"><SelectValue placeholder="Location" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={GENERAL}>General (no location)</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Due date</label>
                <input type="date" data-testid="task-due-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34] focus:ring-1 focus:ring-[#e84e34]" />
              </div>
            </div>
            <DialogFooter>
              <button data-testid="task-submit-button" onClick={createTask} disabled={saving} className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white hover:bg-[#c73e26] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create task
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <TaskImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={loadTasks} />

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
      ) : (
        <div className="flex flex-1 items-start gap-4 overflow-x-auto bg-zinc-50/50 p-6" data-testid="task-board">
          {columns.map((col) => {
            const colTasks = visibleTasks.filter((t) => (col.id ? t.status_id === col.id : true));
            return (
              <div
                key={col.id || "all"}
                onDragOver={(e) => { if (col.id) { e.preventDefault(); setDragOverCol(col.id); } }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={() => { if (col.id) moveTask(dragTaskId.current, col.id); setDragOverCol(null); dragTaskId.current = null; }}
                data-testid={`task-column-${col.id || "all"}`}
                className={`flex w-80 flex-shrink-0 flex-col gap-2 border p-2 transition-colors ${dragOverCol === col.id ? "border-[#e84e34] bg-[#fdece8]" : "border-zinc-200 bg-zinc-100/50"}`}
              >
                <div className="flex items-center justify-between px-1 py-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: col.color }} />
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{col.name}</span>
                  </div>
                  <span className="text-xs text-zinc-400">{colTasks.length}</span>
                </div>
                {colTasks.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`task-card-${t.id}`}
                    draggable
                    onDragStart={(e) => { dragTaskId.current = t.id; e.dataTransfer.effectAllowed = "move"; }}
                    onClick={() => setOpenId(t.id)}
                    className="group relative cursor-grab border border-zinc-200 bg-white p-4 text-left transition-colors hover:border-[#e84e34] active:cursor-grabbing"
                  >
                    {canManage && (
                      <button
                        data-testid={`delete-task-card-${t.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteTask(t.id); }}
                        title="Delete task"
                        className="absolute right-1.5 top-1.5 z-10 hidden p-1 text-zinc-300 transition-colors hover:text-[#e84e34] group-hover:block"
                      ><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                    <div className="flex items-start justify-between gap-2 pr-5">
                      <span className="text-sm font-medium leading-snug text-zinc-900">{t.title}</span>
                      <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: priorityColor(t.priority) }} title={t.priority} />
                    </div>
                    {t.description?.trim() && (() => {
                      const parts = t.description.trim().match(/[^.!?]+[.!?]*/g) || [t.description.trim()];
                      const truncated = parts.length > 2;
                      const snippet = truncated ? parts.slice(0, 2).join(" ").trim() : t.description.trim();
                      return (
                        <p className="mt-1.5 text-xs leading-relaxed text-zinc-500" data-testid={`task-card-desc-${t.id}`}>
                          {snippet}
                          {truncated && <button onClick={(e) => { e.stopPropagation(); setOpenId(t.id); }} data-testid={`task-card-more-${t.id}`} className="ml-1 font-medium text-[#e84e34] hover:underline">More</button>}
                        </p>
                      );
                    })()}
                    {catName(t.category_id) && (
                      <span className="mt-2 inline-block border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">{catName(t.category_id)}</span>
                    )}
                    {t.location_id && locName(t.location_id) && (
                      <span className="mt-2 ml-1 inline-flex items-center gap-0.5 border border-[#e84e34]/30 bg-[#fdece8] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#e84e34]"><MapPin className="h-2.5 w-2.5" />{locName(t.location_id)}</span>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-zinc-400">{t.assignee_name ? `@${t.assignee_name}` : "Unassigned"}</span>
                      <div className="flex items-center gap-2 text-zinc-400">
                        {t.attachments?.length > 0 && <span className="flex items-center gap-0.5 text-xs"><Paperclip className="h-3 w-3" />{t.attachments.length}</span>}
                        {t.activity?.filter((a) => a.type === "comment").length > 0 && <span className="flex items-center gap-0.5 text-xs"><MessageSquare className="h-3 w-3" />{t.activity.filter((a) => a.type === "comment").length}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && <p className="px-1 py-4 text-center text-xs text-zinc-300">No tasks</p>}
              </div>
            );
          })}
        </div>
      )}

      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} statuses={statuses} categories={categories} users={users} locations={locations} onChange={loadTasks} />}
    </div>
  );
}
